/**
 * Quick Fix Mobiles - trade-in quote flow.
 *
 * Two modes, one file:
 *   bundled  - reads the assets/trade-in-prices*.js files. Works with no backend.
 *   api      - set the API base URL in the theme editor and it switches over,
 *              which keeps the price list off the storefront entirely.
 *
 * Booking is recorded by whichever of these is configured, in this order:
 *   1. the intake URL (the Make workflow) - it creates the Zoho lead server
 *      side, checks Zoho actually accepted it, and hands back the reference
 *   2. the browser posting straight to Zoho web-to-lead - no confirmation
 *      possible, so it is the fallback, not the plan
 */
(function () {
  'use strict';
  var root = document.getElementById('qfx-tradein');
  if (!root) return;

  var API = (root.dataset.api || '').replace(/\/+$/, '');
  var BOOK_URL = root.dataset.bookUrl || '/pages/book-a-repair';
  var PHONE = (root.dataset.phone || '').trim();
  // EmailJS sends the customer their copy. Blank settings simply switch it off:
  // the booking still reaches the shop, the customer just is not emailed.
  var EJS = {
    key: (root.dataset.ejsKey || '').trim(),
    service: (root.dataset.ejsService || '').trim(),
    template: (root.dataset.ejsTemplate || '').trim(),
  };
  var EJS_ON = Boolean(EJS.key && EJS.service && EJS.template);
  // The intake workflow records the booking and hands back the reference.
  // When it is set it takes over from the browser-side Zoho post.
  var INTAKE = (root.dataset.intake || '').replace(/\/+$/, '');
  var stage = root.querySelector('.qfx-stage');

  var GRADES = ['As New', 'Working', 'Faulty', 'Dead'];
  // Flat deduction when the customer reports battery health under 80%.
  // Not applied to a Dead device - the battery is irrelevant once it will not power on.
  var BATTERY_DEDUCTION = 75;
  var BATTERY_FAMILIES = ['iPhone', 'Other phones'];

  /** The questions for the current product. The battery question is phones only. */
  function qkeys() {
    var keys = QSETS[S.product.q] || QSETS[2];
    if (BATTERY_FAMILIES.indexOf(S.product.b) === -1) {
      keys = keys.filter(function (k) { return k !== 'battery_health'; });
    }
    return keys;
  }

  var QSETS = {
    0: ['screen_condition', 'case_condition', 'battery_health', 'functionality_faults', 'power_on'],
    1: ['screen_condition', 'overall_condition', 'functionality_faults', 'power_on'],
    2: ['overall_condition', 'functionality_faults', 'power_on']
  };
  var QTEXT = {
    screen_condition: 'How is the screen?',
    case_condition: 'How are the back and sides?',
    overall_condition: 'What condition is it in overall?',
    battery_health: 'Is the battery health above 80%?',
    functionality_faults: 'Any functional faults?',
    power_on: 'Does it power on?'
  };
  // grade index, or -1 meaning "this answer does not downgrade the device"
  var QOPTS = {
    screen_condition: [['Flawless', 0], ['Minor marks', 1], ['Scratches and marks, no cracks', 1], ['Chipped or cracked', 2]],
    case_condition: [['Flawless', 0], ['Minor marks', 1], ['Scratches and marks, no cracks', 1], ['Cracks, chips, bends or dents', 2]],
    overall_condition: [['Flawless', 0], ['Minor marks', 1], ['Scratches and marks, no cracks', 1], ['Chipped or cracked', 2]],
    battery_health: [['Yes, 80% or above', -1], ['No, below 80%', -1]],
    functionality_faults: [['Works perfectly', -1], ['One functional fault', 2], ['Multiple functional faults', 2]],
    power_on: [['Yes, it powers on', -1], ['No, it will not power on', 3]]
  };

  var BOOK = null;
  var BOOK_IS_LIVE = false;   // true only when the book came from the API
  var STORES = [];
  var S = { step: 'family', family: null, product: null, sizeIdx: 0, answers: {}, payout: 'cash',
            route: null, sending: false, result: null, error: null, contact: null };

  var money = function (n) { return '$' + Number(n).toLocaleString('en-AU'); };
  var esc = function (s) {
    return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  };
  var round5 = function (n) { return Math.max(5, Math.round(n / 5) * 5); };

  /* ---------------- pricing ---------------- */
  function grade() {
    if (!S.product) return null;
    var keys = qkeys();
    var worst = 0;
    for (var i = 0; i < keys.length; i++) {
      var idx = S.answers[keys[i]];
      if (idx === undefined) return null;
      var g = QOPTS[keys[i]][idx][1];
      if (g > worst) worst = g;      // -1 (a pass) can never raise the worst grade
    }
    return worst;
  }
  function cash() {
    var g = grade();
    if (g === null || !S.product) return null;
    var v = S.product.s[S.sizeIdx][1 + g];
    if (v === null || v === undefined) return null;
    if (g !== 3 && S.answers.battery_health === 1) {
      v = v - BATTERY_DEDUCTION;
      if (v <= 0) return null;   // too little left to quote online - price it in person
    }
    return v;
  }
  // Store credit was retired in Oct 2026: every trade-in is paid in cash, so
  // credit() simply mirrors cash() to keep the CRM/intake payloads stable.
  function credit() { return cash(); }
  function amount() { return cash(); }

  function families() {
    var order = ['iPhone', 'Other phones', 'Tablets', 'MacBook', 'Laptops',
                 'Smart watches', 'PlayStation & consoles', 'Audio & headphones', 'Accessories'];
    var counts = {};
    BOOK.products.forEach(function (p) { counts[p.b] = (counts[p.b] || 0) + 1; });
    return order.filter(function (f) { return counts[f]; })
                .map(function (f) { return { name: f, n: counts[f] }; });
  }

  /* ---------------- screens ---------------- */
  function go(step) {
    S.step = step;
    render();
    var top = root.getBoundingClientRect().top + window.pageYOffset - 20;
    window.scrollTo({ top: top, behavior: 'smooth' });
  }

  function vFamily() {
    return head('Step 1 of 5', 'What are you trading in?',
             BOOK.products.length.toLocaleString('en-AU') + ' devices priced right now. Pick a family to narrow it down.') +
      '<div class="qfx-tiles">' + families().map(function (f) {
        return '<button type="button" class="qfx-tile" data-family="' + esc(f.name) + '">' +
          '<span class="qfx-tile-t">' + esc(f.name) + '</span>' +
          '<span class="qfx-tile-c">' + f.n + ' model' + (f.n === 1 ? '' : 's') + '</span></button>';
      }).join('') + '</div>';
  }

  function vDevice() {
    return head('Step 2 of 5 &middot; ' + esc(S.family), 'Find your model', 'Start typing, or scroll the list.') +
      '<label class="qfx-sr-only" for="qfx-search">Search models</label>' +
      '<input id="qfx-search" class="qfx-search" type="search" placeholder="Search ' + esc(S.family) + '&hellip;" autocomplete="off">' +
      '<div class="qfx-list" id="qfx-list"></div>' +
      '<p class="qfx-count" id="qfx-count"></p>' +
      '<div class="qfx-acts"><button type="button" class="qfx-back" data-go="family">Back</button></div>';
  }
  function paintList() {
    var box = document.getElementById('qfx-list');
    if (!box) return;
    var term = (document.getElementById('qfx-search').value || '').toLowerCase().trim();
    var all = BOOK.products.filter(function (p) { return p.b === S.family; });
    var hits = all.filter(function (p) { return !term || p.n.toLowerCase().indexOf(term) > -1; });
    var show = hits.slice(0, 80);
    box.innerHTML = show.length ? show.map(function (p) {
      var best = 0;
      p.s.forEach(function (row) { for (var i = 1; i <= 4; i++) if (row[i] > best) best = row[i]; });
      var top = best;
      return '<button type="button" class="qfx-row" data-product="' + p.i + '">' +
        '<span class="qfx-row-n">' + esc(p.n) + '</span>' +
        '<span class="qfx-row-p">up to ' + money(top) + '</span></button>';
    }).join('')
      : '<p class="qfx-none">No match. Try a shorter search &mdash; or <a href="' + esc(BOOK_URL) + '">ask us directly</a> and we\'ll quote it.</p>';
    document.getElementById('qfx-count').textContent =
      hits.length ? 'Showing ' + show.length + ' of ' + hits.length + ' models' : '';
  }

  function vSize() {
    return head('Step 3 of 5', esc(S.product.n), 'Which storage size is it?') +
      '<div class="qfx-opts">' + S.product.s.map(function (row, i) {
        var best = 0;
        for (var k = 1; k <= 4; k++) if (row[k] > best) best = row[k];
        return '<button type="button" class="qfx-opt' + (i === S.sizeIdx ? ' is-on' : '') + '" data-size="' + i + '">' +
          '<span class="qfx-dot"></span><span class="qfx-mono">' +
          esc(row[0] === 'other_tradein' ? 'Standard' : row[0]) + '</span>' +
          '<span class="qfx-opt-x">up to ' + money(best) + '</span></button>';
      }).join('') + '</div>' +
      '<div class="qfx-acts"><button type="button" class="qfx-back" data-go="device">Back</button>' +
      '<button type="button" class="qfx-btn" data-go="condition">Continue</button></div>';
  }

  function vCondition() {
    var keys = qkeys();
    var ready = grade() !== null;
    return head('Step 4 of 5 &middot; ' + esc(S.product.n), 'Tell us its condition',
             'Answer honestly. We check every device before we pay, and you can walk away from any revised offer.') +
      keys.map(function (k) {
        return '<fieldset class="qfx-q"><legend>' + QTEXT[k] + '</legend><div class="qfx-opts">' +
          QOPTS[k].map(function (o, i) {
            return '<button type="button" class="qfx-opt' + (S.answers[k] === i ? ' is-on' : '') +
              '" data-q="' + k + '" data-i="' + i + '"><span class="qfx-dot"></span>' + esc(o[0]) + '</button>';
          }).join('') + '</div></fieldset>';
      }).join('') +
      '<div class="qfx-acts"><button type="button" class="qfx-back" data-go="size">Back</button>' +
      '<button type="button" class="qfx-btn" data-go="offer"' + (ready ? '' : ' disabled') + '>See my offer</button></div>' +
      '<details class="qfx-calc"><summary>How we work out the grade</summary>' +
      '<p>Every answer maps to a grade, and your device takes the <strong>lowest grade you selected</strong> &mdash; not an average. ' +
      'A flawless screen with one functional fault grades as Faulty. Anything that will not power on grades as Dead, whatever else is true. ' +
      'On phones, battery health under 80% takes $' + BATTERY_DEDUCTION + ' off the offer.</p>' +
      '</details>';
  }

  function vOffer() {
    var g = grade(), c = cash();
    if (c === null) {
      return head('Your offer', esc(S.product.n), '') +
        '<div class="qfx-flag"><p><strong>We price this one in person.</strong> ' +
        'We buy this device but not at this exact condition and size combination, so we\'d rather look at it than guess. ' +
        'Bring it into Parramatta or send us a photo and we\'ll quote it the same day.</p></div>' +
        '<div class="qfx-acts"><button type="button" class="qfx-back" data-go="condition">Change answers</button>' +
        '<a class="qfx-btn" href="' + esc(BOOK_URL) + '">Get an in-person quote</a></div>';
    }
    var repair = (g === 2 || g === 3);
    return head('Your offer', esc(S.product.n), '') +
      '<p class="qfx-figure">' + money(amount()) + '<span>cash</span></p>' +
      '<p class="qfx-graded">Graded <span class="qfx-pill">' + GRADES[g] + '</span></p>' +
      '<p class="qfx-pay-note">Paid by bank transfer within 2 business days of us checking the device.</p>' +
      (repair ? '<div class="qfx-flag qfx-flag--win"><p><strong>Damaged? Even better.</strong> ' +
        'Most trade-in sites drop a broken device to a fraction of its value, because a cracked screen is someone else\'s problem. ' +
        'We repair phones in Parramatta every day, so we can pay properly for one.</p></div>' : '') +
      '<div class="qfx-acts"><button type="button" class="qfx-back" data-go="condition">Change answers</button>' +
      '<button type="button" class="qfx-btn" data-go="handover">Continue &mdash; ' + money(amount()) + '</button></div>' +
      '<p class="qfx-fine">Held for 14 days. Confirmed once we have the device in front of us &mdash; if our check disagrees ' +
      'with your answers we send a revised offer, and you can accept it or have the device back at no cost.</p>';
  }

  function vHandover() {
    return head('Step 5 of 5 &middot; ' + money(amount()) + ' cash',
             'How do we get the device?', 'Same offer either way.') +
      '<div class="qfx-routes">' +
        '<button type="button" class="qfx-route' + (S.route === 'store' ? ' is-on' : '') + '" data-route="store">' +
          '<span class="qfx-route-h">Bring it in</span>' +
          '<span class="qfx-route-p">Fastest &mdash; checked while you wait and paid the same day.</span></button>' +
        '<button type="button" class="qfx-route' + (S.route === 'post' ? ' is-on' : '') + '" data-route="post">' +
          '<span class="qfx-route-h">Post it to us</span>' +
          '<span class="qfx-route-p">Free prepaid, insured label. Free return if you say no.</span></button>' +
      '</div>' +
      (S.route ? form() : '') +
      '<div class="qfx-acts"><button type="button" class="qfx-back" data-go="offer">Back</button>' +
      '<button type="button" class="qfx-btn" data-go="done"' + (S.route ? '' : ' disabled') + '>Confirm trade-in</button></div>';
  }
  function form() {
    return '<div class="qfx-fields">' +
      field('qfx-name', 'Your name', 'text', 'name', 'full_name') +
      field('qfx-email', 'Email', 'email', 'email', 'email') +
      field('qfx-phone', 'Mobile', 'tel', 'tel', 'phone') +
      (S.route === 'post' ? field('qfx-addr', 'Pickup address', 'text', 'street-address', 'address') : '') +
      '</div>';
  }
  function field(id, label, type, ac, key) {
    var prior = (S.contact && S.contact[key]) || '';
    return '<label class="qfx-field" for="' + id + '"><span>' + label + '</span>' +
      '<input id="' + id + '" type="' + type + '" autocomplete="' + ac + '"' +
      (prior ? ' value="' + esc(prior) + '"' : '') + '></label>';
  }

  function vDone() {
    // Nothing is confirmed until the send has finished. Three states, and the
    // failure state deliberately does NOT hand out a reference: a reference
    // that exists only in this browser is worse than no reference at all,
    // because the customer arrives at the counter believing they have a deal.
    if (S.sending) {
      return head('One moment', 'Booking your trade-in&hellip;',
                  'Saving your details and locking in your price.') +
        '<div class="qfx-wait" role="status" aria-live="polite">' +
        '<span class="qfx-spin" aria-hidden="true"></span>' +
        '<p>This usually takes a second or two.</p></div>';
    }

    if (S.error) {
      return head('Not booked', 'We could not save that', S.error) +
        '<div class="qfx-flag"><p><strong>Nothing has been booked, so please do not send your device yet.</strong> ' +
        'Your quote of ' + money(amount()) + ' still stands &mdash; ' +
        (PHONE
          ? 'call us on <a href="tel:' + esc(PHONE.replace(/[^+0-9]/g, '')) + '">' + esc(PHONE) + '</a> or '
          : '') +
        '<a href="' + esc(BOOK_URL) + '">get in touch</a> and we will finish it off by hand.</p></div>' +
        '<div class="qfx-acts"><button type="button" class="qfx-back" data-go="handover">Back</button>' +
        '<button type="button" class="qfx-btn" data-go="retry">Try again</button></div>';
    }

    var r = S.result || {};
    var ref = r.reference;
    var post = S.route === 'post';
    var paid = money(r.offer_aud != null ? r.offer_aud : amount());
    // Without an automated label there is no tracking, so the promises differ.
    // Saying "your label is on the way" when no system exists to send one would
    // be a lie the shop then has to live with.
    var steps = r.by_email
      ? (post
        ? [['We have your details', 'Your trade-in has just landed with us against this reference.'],
           ['We send your postage label', 'One of us emails you a prepaid, insured label, usually the same day.'],
           ['Checked, then paid', paid + ' once we have looked it over &mdash; or a revised offer you are free to refuse, and we post it back at no cost.']]
        : [['We have your details', 'Your trade-in has just landed with us against this reference.'],
           ['Bring it in within 14 days', 'Shop 7A, 118 Church Street, Parramatta. Quote this reference and bring photo ID.'],
           ['Checked while you wait', 'Usually under 15 minutes, then ' + paid + ' ' + (S.payout === 'credit' ? 'as store credit' : 'to your bank') + '.']])
      : (post
        ? [['Your label is on the way', 'Prepaid, insured and trackable. Check your email. You have 14 days to send it.'],
           ['We let you know it landed', 'Then it goes to a technician to be checked.'],
           ['Paid within 2 business days', 'Or, if our check differs, you get a revised offer &mdash; accept it or we post it back free.']]
        : [['Bring it in within 14 days', 'Quote this reference at the counter, and bring photo ID.'],
           ['Checked while you wait', 'Usually under 15 minutes.'],
           ['Paid on the spot', paid + ' ' + (S.payout === 'credit' ? 'as store credit' : 'to your bank') + '.']]);
    return head('Confirmed', 'Your trade-in is booked',
             r.customer_emailed
               ? 'We have emailed your reference to ' + esc(r.customer_emailed) +
                 '. It holds your ' + paid + ' offer for 14 days.'
               : 'Keep this reference &mdash; it holds your ' + paid + ' offer for 14 days.') +
      '<p class="qfx-ref">' + esc(ref) + '</p>' +
      '<ol class="qfx-steps">' + steps.map(function (s) {
        return '<li><strong>' + s[0] + '</strong><span>' + s[1] + '</span></li>';
      }).join('') + '</ol>' +
      '<div class="qfx-flag"><p><strong>Before you hand it over:</strong> back up anything you want to keep, ' +
      'sign out of iCloud or your Google account and turn off Find My, and take out your SIM. ' +
      'A device that is still locked cannot be paid at the quoted price.</p></div>' +
      '<div class="qfx-acts"><button type="button" class="qfx-back" data-go="reset">Quote another device</button></div>';
  }

  /* ---------------- submitting ---------------- */

  /** Read the contact fields while they are still in the DOM. */
  function readForm() {
    var val = function (id) {
      var el = document.getElementById(id);
      return el ? el.value.trim() : '';
    };
    var prev = S.contact || {};
    var keep = function (id, was) {
      return document.getElementById(id) ? val(id) : (was || '');
    };
    return {
      full_name: keep('qfx-name', prev.full_name),
      email: keep('qfx-email', prev.email),
      phone: keep('qfx-phone', prev.phone),
      address: keep('qfx-addr', prev.address),
    };
  }

  function postJSON(path, body) {
    return fetch(API + path, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    }).then(function (r) {
      return r.json().catch(function () { return {}; }).then(function (j) {
        if (!r.ok) throw new Error(j.error || ('Request failed (' + r.status + ')'));
        return j;
      });
    });
  }

  /**
   * Second copy of the booking: Shopify's own contact endpoint, which emails
   * the store's customer-service address. Never load-bearing - Shopify's bot
   * guard can refuse a plain script submission, so it is attempted and any
   * failure ignored. Only used on the no-intake fallback path.
   */
  function postContactForm(fields) {
    var body = new URLSearchParams();
    body.set('form_type', 'contact');
    body.set('utf8', '✓');
    Object.keys(fields).forEach(function (k) {
      if (fields[k]) body.set('contact[' + k + ']', fields[k]);
    });
    return fetch('/contact', {
      method: 'POST',
      headers: { 'content-type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
    }).then(function (r) {
      var posted = r.redirected || /contact_posted/.test(r.url || '');
      if (!r.ok || !posted) throw new Error('Your booking did not go through.');
      return true;
    });
  }

  /** A reference the shop can search their CRM for. Fallback path only. */
  function makeRef() {
    var d = new Date();
    var pad = function (n) { return (n < 10 ? '0' : '') + n; };
    var rand = '';
    for (var i = 0; i < 4; i++) {
      rand += 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'[Math.floor(Math.random() * 32)];
    }
    return 'QFM-' + pad(d.getMonth() + 1) + pad(d.getDate()) + '-' + rand;
  }

  /** Everything the counter needs, in a note that reads top to bottom. */
  function bookingEmail(ref, c) {
    var size = S.product.s[S.sizeIdx][0];
    return [
      'TRADE-IN BOOKING - ' + ref,
      '',
      (S.route === 'post' ? 'POSTING IT IN' : 'WALK-IN') + ' - ' + money(amount()) +
        ' as ' + (S.payout === 'credit' ? 'STORE CREDIT' : 'CASH'),
      '',
      'Device:    ' + S.product.n + (size !== 'other_tradein' ? ' ' + size : ''),
      'Graded:    ' + GRADES[grade()] + ' (from the answers below)',
      'Offer:     ' + money(cash()) + ' cash',
      '',
      'Name:      ' + (c.full_name || '(not given)'),
      'Email:     ' + (c.email || '(not given)'),
      'Mobile:    ' + (c.phone || '(not given)'),
      (S.route === 'post' ? 'Send label to: ' + c.address : 'Bringing it into Parramatta'),
      '',
      'Their answers:',
      qkeys().map(function (k) {
        return '  ' + QTEXT[k] + '  ->  ' + QOPTS[k][S.answers[k]][0];
      }).join('\n'),
      '',
      'Quoted ' + new Date().toLocaleString('en-AU') + '. Held 14 days.',
    ].join('\n');
  }

  /**
   * The customer's copy, sent straight from the browser through EmailJS.
   * Deliberately NOT load-bearing - a courtesy on top of the CRM record, and
   * switched off entirely once the intake workflow sends the email itself.
   */
  function emailCustomer(ref, c) {
    if (!EJS_ON || !c.email) return Promise.resolve(false);
    var size = S.product.s[S.sizeIdx][0];
    var post = S.route === 'post';
    return fetch('https://api.emailjs.com/api/v1.0/email/send', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        service_id: EJS.service,
        template_id: EJS.template,
        user_id: EJS.key,
        template_params: {
          to_email: c.email,
          to_name: c.full_name || 'there',
          reference: ref,
          device: S.product.n + (size !== 'other_tradein' ? ' ' + size : ''),
          grade: GRADES[grade()],
          amount: money(amount()),
          payout: S.payout === 'credit' ? 'store credit' : 'cash',
          handover: post ? 'posting it to us' : 'bringing it into our Parramatta shop',
          next_step: post
            ? 'We will email you a prepaid, insured postage label, usually the same day. You have 14 days to send it.'
            : 'Bring it into Shop 7A, 118 Church Street, Parramatta within 14 days. Quote your reference and bring photo ID.',
          address: c.address || '',
          shop_name: 'Quick Fix Mobiles',
          shop_phone: PHONE || '',
          shop_url: 'https://quickfixmobiles.com.au',
        },
      }),
    }).then(function (r) { return r.ok; }).catch(function () { return false; });
  }

  /**
   * Send the booking to the intake workflow.
   *
   * The reference comes BACK from the workflow rather than being invented
   * here. That is the whole point: the workflow creates the CRM record, reads
   * the CRM's actual answer, and only then hands over a reference - so a
   * reference the customer can see is one the shop can look up.
   */
  function postIntake(c) {
    var size = S.product.s[S.sizeIdx][0];
    var answers = {};
    qkeys().forEach(function (k) {
      answers[k] = QOPTS[k][S.answers[k]][0];
    });
    return fetch(INTAKE, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        device: S.product.n,
        size: size === 'other_tradein' ? '' : size,
        grade: GRADES[grade()],
        channel: S.route === 'post' ? 'post_in' : 'walk_in',
        offer_cash: cash(),
        offer_credit: credit(),
        payout: S.payout === 'credit' ? 'credit' : 'cash',
        name: c.full_name || '',
        email: c.email || '',
        phone: c.phone || '',
        address: c.address || '',
        answers: answers,
      }),
    }).then(function (r) {
      return r.json().catch(function () { return {}; }).then(function (j) {
        if (!r.ok || !j.reference) {
          throw new Error(j.error || 'We could not save your booking just now.');
        }
        return j;
      });
    });
  }

  /**
   * Fallback: push the booking into Zoho CRM straight from the browser.
   *
   * Only runs when no intake URL is set. Two things matter here and both have
   * bitten us:
   *
   * 1. This MUST return its promise. It used to fire the request and return
   *    undefined, so the caller's .then() threw a TypeError mid-click and the
   *    customer sat on the "Booking your trade-in..." spinner forever.
   *
   * 2. mode:'no-cors'. Zoho's WebToLeadForm endpoint sends no CORS headers, so
   *    an ordinary cross-origin fetch REJECTS even though the POST arrives and
   *    the lead is created. An opaque request is delivered and resolves, which
   *    is the right shape for a fire-and-forget form post. The trade-off: we
   *    cannot read Zoho's answer, so a Zoho-side rejection looks like success.
   *    That blind spot is exactly what the intake workflow removes.
   */
  function zohoLead(c, ref) {
    try {
      var sizeRow = (S.product && S.product.s && S.product.s[S.sizeIdx]) ? S.product.s[S.sizeIdx][0] : '';
      var size = sizeRow === 'other_tradein' ? '' : sizeRow;
      var payoutPref = S.payout === 'credit' ? 'Store Credit' : 'Cash';
      var handoverMethod = S.route === 'post' ? 'Post it in' : 'Bring in-store';
      var fullName = (c.full_name || '').trim();
      var sp = fullName.indexOf(' ');
      var firstName = sp > -1 ? fullName.slice(0, sp) : '';
      var lastName = sp > -1 ? fullName.slice(sp + 1) : (fullName || 'Trade-in customer');
      var fd = new URLSearchParams();
      fd.set('xnQsjsdp', 'd41710efdc8859c13c1c79da2d9181ae92d212188ef3f7b37e6e6bb1005c1d6f');
      fd.set('zc_gad', '');
      fd.set('xmIwtLD', '066809808807a2262f13dd9f2d51c30b42f16b0c64e1689abcaa8630a708e7b0f3398d8c1543f5ec7c63b42f28748a66');
      fd.set('actionType', 'TGVhZHM=');
      fd.set('returnURL', 'https://quickfixmobiles.com.au/pages/buyback');
      fd.set('Company', fullName || 'Trade-in customer');
      fd.set('Last Name', lastName);
      fd.set('First Name', firstName);
      fd.set('Email', c.email || '');
      fd.set('Mobile', c.phone || '');
      fd.set('Lead Source', 'Website Trade-In Form');
      fd.set('Cash Offer', cash() != null ? String(cash()) : '');
      fd.set('Store Credit Offer', credit() != null ? String(credit()) : '');
      fd.set('Trade-in Reference', ref || '');
      fd.set('Device Model', S.product ? S.product.n : '');
      fd.set('Storage Size', size || '');
      fd.set('Payout Preference', payoutPref);
      fd.set('Handover Method', handoverMethod);
      if (c.address) fd.set('Street', c.address);
      fd.set('Description', bookingEmail(ref, c));
      return fetch('https://crm.zoho.com.au/crm/WebToLeadForm', {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'content-type': 'application/x-www-form-urlencoded' },
        body: fd.toString()
      }).then(function () { return true; }).catch(function () { return false; });
    } catch (e) {
      return Promise.resolve(false);
    }
  }

  /** Commit the trade-in. */
  function submit() {
    var c = S.contact = readForm();
    if (!c.email && !c.phone) {
      S.error = 'We need an email address or a mobile number so we can reach you.';
      S.result = null;
      return go('done');
    }
    if (S.route === 'post' && !c.address) {
      S.error = 'We need a pickup address to send your prepaid postage label to.';
      S.result = null;
      return go('done');
    }

    // Intake workflow: it creates the CRM record, verifies the CRM accepted
    // it, and returns the reference. Preferred whenever it is configured.
    if ((!API || !BOOK_IS_LIVE) && INTAKE) {
      S.sending = true; S.error = null; S.result = null;
      go('done');
      return postIntake(c).then(function (j) {
        // No zohoLead() call here on purpose. The workflow already filed this
        // booking in Zoho; posting it from the browser as well would create a
        // second, duplicate lead for the same customer.
        return emailCustomer(j.reference, c).then(function (sent) {
          S.sending = false;
          S.result = { reference: j.reference, offer_aud: amount(), by_email: true,
                       in_crm: true, customer_emailed: sent ? c.email : null };
          render();
        });
      }).catch(function (err) {
        S.sending = false; S.result = null;
        S.error = String(err && err.message || 'Something went wrong at our end.');
        render();
      });
    }

    // BOOK_IS_LIVE guards a nasty case: the API is configured but its price
    // book did not load, so we are showing bundled data whose ids are array
    // indexes. Posting one of those would book somebody else's device.
    if (!API || !BOOK_IS_LIVE) {
      // No API and no intake workflow, so the booking goes straight to Zoho
      // from the browser, with the Shopify contact email as a second copy.
      //
      // The whole chain sits inside Promise.resolve().then(...) with a catch
      // on the end, so anything that throws in here lands on the error screen
      // instead of freezing the spinner.
      var ref = makeRef();
      S.sending = true;
      S.error = null;
      S.result = null;
      go('done');

      return Promise.resolve()
        .then(function () { return zohoLead(c, ref); })
        .then(function (savedToZoho) {
          try {
            postContactForm({
              name: c.full_name || 'Trade-in customer',
              email: c.email || 'noreply@quickfixmobiles.com.au',
              phone: c.phone || '',
              body: bookingEmail(ref, c),
            }).catch(function () {});
          } catch (e) { /* second copy only - never blocks the booking */ }

          if (!savedToZoho) {
            throw new Error('We could not reach our booking system just now.');
          }

          return emailCustomer(ref, c).then(function (sent) {
            S.sending = false;
            S.result = { reference: ref, offer_aud: amount(), by_email: true,
                         customer_emailed: sent ? c.email : null };
            render();
          });
        })
        .catch(function (err) {
          S.sending = false;
          S.result = null;
          S.error = String(err && err.message || 'Something went wrong at our end.');
          render();
        });
    }

    S.sending = true; S.error = null; S.result = null;
    go('done');

    var channel = S.route === 'post' ? 'online' : 'in_store';
    postJSON('/api/quote', {
      product_id: S.product.i,
      size: S.product.s[S.sizeIdx][0],
      answers: S.answers,
      channel: channel,
    }).then(function (q) {
      return postJSON('/api/accept', {
        quote_id: q.quote_id,
        payout_method: S.payout === 'credit' ? 'credit' : 'cash',
        channel: channel,
        store_id: channel === 'in_store' ? (STORES[0] && STORES[0].id) : undefined,
        customer: {
          full_name: c.full_name || null,
          email: c.email || null,
          phone: c.phone || null,
          address: c.address || null,
        },
      });
    }).then(function (t) {
      S.sending = false; S.result = t; S.error = null;
      render();
    }).catch(function (err) {
      S.sending = false; S.result = null;
      S.error = String(err && err.message || 'Something went wrong at our end.');
      render();
    });
  }

  function head(eyebrow, title, sub) {
    return '<p class="qfx-eyebrow">' + eyebrow + '</p><h2 class="qfx-h">' + title + '</h2>' +
      (sub ? '<p class="qfx-sub">' + sub + '</p>' : '');
  }

  /* ---------------- render + events ---------------- */
  var VIEWS = { family: vFamily, device: vDevice, size: vSize, condition: vCondition,
                offer: vOffer, handover: vHandover, done: vDone };

  function render() {
    stage.innerHTML = VIEWS[S.step]();
    stage.setAttribute('aria-busy', 'false');
    var search = document.getElementById('qfx-search');
    if (search) { paintList(); search.addEventListener('input', paintList); }
  }

  stage.addEventListener('click', function (ev) {
    var t = ev.target.closest('[data-family],[data-product],[data-size],[data-q],[data-pay],[data-route],[data-go]');
    if (!t || t.disabled) return;
    if (document.getElementById('qfx-email')) S.contact = readForm();
    var d = t.dataset;
    if (d.family !== undefined) { S.family = d.family; return go('device'); }
    if (d.product !== undefined) {
      S.product = BOOK.products.filter(function (p) { return String(p.i) === d.product; })[0];
      S.sizeIdx = 0; S.answers = {};
      return go(S.product.s.length > 1 && S.product.s[0][0] !== 'other_tradein' ? 'size' : 'condition');
    }
    if (d.size !== undefined) { S.sizeIdx = Number(d.size); return render(); }
    if (d.q !== undefined) { S.answers[d.q] = Number(d.i); return render(); }
    if (d.pay !== undefined) { S.payout = d.pay; return render(); }
    if (d.route !== undefined) { S.route = d.route; return render(); }
    if (d.go !== undefined) {
      // Leaving the handover step means committing. Capture what they typed
      // before render() throws the inputs away.
      if (d.go === 'done' || d.go === 'retry') {
        // A throw inside submit() used to escape this handler and leave the
        // spinner up forever. It cannot any more, but the belt stays on.
        try {
          return submit();
        } catch (e) {
          S.sending = false;
          S.result = null;
          S.error = String(e && e.message || 'Something went wrong at our end.');
          return render();
        }
      }
      if (d.go === 'reset') {
        S = { step: 'family', family: null, product: null, sizeIdx: 0, answers: {},
             payout: 'cash', route: null, sending: false, result: null, error: null, contact: null };
        return go('family');
      }
      return go(d.go);
    }
  });

  /* ---------------- boot ---------------- */
  var B36 = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';

  // The bundled price list is packed to keep the storefront download small.
  //   head line 1: "Family:questionSet~Family:questionSet~..."
  //   head line 2: the size vocabulary, indexed by a single character
  //   each row:    "Device name|familyIndex|sizeChar,asNew,working,faulty,dead;..."
  //   prices are base36; a trailing blank means we do not buy at that grade
  function parseBook() {
    var head = String(window.QFX_PB_HEAD || '').split('\n');
    var fams = head[0].split('~').map(function (f) { return f.split(':'); });
    var sizes = (head[1] || '').split('~');
    var products = [];
    String((window.QFX_PB_ROWS || []).join('\n')).split('\n').forEach(function (line, idx) {
      if (!line) return;
      var f = line.split('|');
      var fam = fams[Number(f[1])] || ['Other', '0'];
      products.push({
        i: idx,
        n: f[0],
        b: fam[0],
        q: Number(fam[1]),
        s: f[2].split(';').map(function (chunk) {
          var c = chunk.split(',');
          var row = [sizes[B36.indexOf(c[0])]];
          for (var k = 1; k <= 4; k++) {
            row.push(c[k] === undefined || c[k] === '' ? null : parseInt(c[k], 36));
          }
          return row;
        })
      });
    });
    return { credit_bonus_pct: Number(root.dataset.creditBonus || 20), products: products };
  }

  function boot(book, live) { BOOK = book; BOOK_IS_LIVE = !!live; render(); }

  function failed(message) {
    stage.setAttribute('aria-busy', 'false');
    stage.innerHTML = '<div class="qfx-flag"><p><strong>' + esc(message) + '</strong> ' +
      'Give us a call or <a href="' + esc(BOOK_URL) + '">book a visit</a> and we\'ll quote your device directly.</p></div>';
  }

  if (API) {
    // Live mode: the API prices everything and the list never reaches the browser.
    fetch(API + '/api/stores')
      .then(function (r) { return r.ok ? r.json() : []; })
      .then(function (list) { STORES = Array.isArray(list) ? list : (list.stores || []); })
      .catch(function () { STORES = []; });

    fetch(API + '/api/price-book')
      .then(function (r) { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then(function (book) { boot(book, true); })
      .catch(function () { fallback(); });
  } else {
    fallback();
  }

  function fallback() {
    if (window.QFX_PB_HEAD && window.QFX_PB_ROWS) return boot(parseBook(), false);
    failed('Our quote tool is having a moment.');
  }
})();
